import { useRef, useState } from "react";
import { validateLead, type LeadErrorCode, type LeadValues } from "../../shared/lead";
import { clarityEvent } from "../lib/clarity";
import { useI18n } from "../i18n/I18nProvider";
import { AreaChooser } from "./AreaChooser";

const FILL_MIN_MS = import.meta.env.VITE_E2E ? 0 : 2000;

type FieldKey = "fullName" | "phone" | "email" | "company" | "area" | "notes";

interface FieldState {
  value: string;
  error: LeadErrorCode | null;
}

const emptyFields: Record<FieldKey, FieldState> = {
  fullName: { value: "", error: null },
  phone: { value: "", error: null },
  email: { value: "", error: null },
  company: { value: "", error: null },
  area: { value: "", error: null },
  notes: { value: "", error: null },
};

/**
 * The form owns the fields, the validation and the anti-bot checks. The page owns the network
 * phase, so a valid submit hands the values up and the whole page can swap to the status view.
 */
export function LeadForm({
  onSubmit,
  onBypass,
}: {
  onSubmit: (values: LeadValues) => void;
  onBypass: () => void;
}) {
  const { copy } = useI18n();
  const t = copy.form;
  const openAt = useRef<number>(Date.now());

  const [fields, setFields] = useState<Record<FieldKey, FieldState>>(emptyFields);
  const [website, setWebsite] = useState("");

  const errorText = (code: LeadErrorCode | null) => {
    if (!code) return null;
    const names: Record<LeadErrorCode, keyof typeof copy.errors> = {
      required: "required",
      too_short: "tooShort",
      too_long: "tooLong",
      invalid_phone: "invalidPhone",
      invalid_email: "invalidEmail",
      invalid_area: "invalidArea",
    };
    return copy.errors[names[code]];
  };

  const setField = (key: FieldKey, value: string) => {
    setFields((prev) => ({ ...prev, [key]: { value, error: null } }));
  };

  function handleSubmit() {
    const result = validateLead({
      fullName: fields.fullName.value,
      phone: fields.phone.value,
      email: fields.email.value,
      company: fields.company.value,
      area: fields.area.value,
      notes: fields.notes.value,
    });

    if (!result.ok) {
      setFields((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next) as FieldKey[]) {
          next[key] = { ...prev[key], error: result.errors[key] ?? null };
        }
        return next;
      });
      clarityEvent("lead_invalid");
      return;
    }

    if (website !== "") {
      onBypass();
      return;
    }
    if (Date.now() - openAt.current < FILL_MIN_MS) {
      onBypass();
      return;
    }

    onSubmit(result.value);
  }

  return (
    <section
      id="lead-form"
      aria-label={t.title}
      className="scroll-mt-24 lg:flex lg:w-full lg:flex-col"
    >
      {/* Mobile frame 5:150 keeps the heading on the navy band above a separate
          white card; desktop frame 5:2 puts the heading inside the card. */}
      <div className="mb-5 lg:hidden">
        <h2 className="text-right text-[28px] font-bold leading-[36px] text-white">{t.title}</h2>
        <p className="mt-1.5 text-[12px] leading-[18px] text-[#dcdeec]">{t.intro}</p>
      </div>

      <div className="flex flex-col gap-[13px] rounded-[18px] bg-white p-4 shadow-panel lg:flex-1 lg:gap-[18px] lg:rounded-[22px] lg:px-10 lg:pb-[9px] lg:pt-[37px]">
        <div className="hidden lg:block">
          <h2 className="text-[34px] font-bold leading-[44.2px] text-navy">{t.title}</h2>
          <p className="mt-[18px] text-[14px] leading-[22.4px] text-body">{t.intro}</p>
        </div>

        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
          className="flex flex-col gap-[13px] lg:flex-1 lg:justify-between lg:gap-[18px]"
        >
          <div className="grid grid-cols-1 gap-[13px] lg:grid-cols-2 lg:gap-[14px]">
            <TextField
              id="fullName"
              label={t.fullName}
              placeholder={t.fullNamePlaceholder}
              value={fields.fullName.value}
              error={errorText(fields.fullName.error)}
              errorId="fullName-error"
              onChange={(value) => setField("fullName", value)}
              autoComplete="name"
              required
            />
            <TextField
              id="phone"
              label={t.phone}
              placeholder={t.phonePlaceholder}
              value={fields.phone.value}
              error={errorText(fields.phone.error)}
              errorId="phone-error"
              onChange={(value) => setField("phone", value)}
              autoComplete="tel"
              inputMode="tel"
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-[13px] lg:grid-cols-2 lg:gap-[14px]">
            <TextField
              id="company"
              label={t.company}
              placeholder={t.companyPlaceholder}
              value={fields.company.value}
              error={errorText(fields.company.error)}
              errorId="company-error"
              onChange={(value) => setField("company", value)}
              autoComplete="organization"
            />
            <TextField
              id="email"
              label={t.email}
              placeholder={t.emailPlaceholder}
              value={fields.email.value}
              error={errorText(fields.email.error)}
              errorId="email-error"
              onChange={(value) => setField("email", value)}
              autoComplete="email"
              inputMode="email"
            />
          </div>

          <AreaChooser
            value={fields.area.value}
            onChange={(value) => setField("area", value)}
            invalid={fields.area.error !== null}
            errorId="area-error"
            error={errorText(fields.area.error)}
          />

          <TextArea
            id="notes"
            label={t.notes}
            placeholder={t.notesPlaceholder}
            value={fields.notes.value}
            error={errorText(fields.notes.error)}
            errorId="notes-error"
            onChange={(value) => setField("notes", value)}
          />

          <label className="sr-only" aria-hidden="true">
            {t.company}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
          </label>

          <button
            type="submit"
            className="gradient-cta mt-1 flex h-[50px] w-full items-center justify-center rounded-xl text-[13px] font-bold text-white shadow-card transition-opacity disabled:opacity-70 lg:h-[54px] lg:text-[14px]"
          >
            {t.submit}
          </button>
          <p className="-mt-2 text-center text-[10px] leading-[18.7px] text-body">{t.privacy}</p>
        </form>
      </div>
    </section>
  );
}

function TextField({
  id,
  label,
  placeholder,
  value,
  error,
  errorId,
  onChange,
  autoComplete,
  inputMode,
  className,
  required,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  error: string | null;
  errorId: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  inputMode?: "tel" | "email" | "numeric" | "text";
  className?: string;
  required?: boolean;
}) {
  const invalid = error !== null;
  return (
    <div className={`flex flex-col gap-[7px] ${className ?? ""}`}>
      <label htmlFor={id} className="text-[13px] font-semibold leading-[24.4px] text-navy">
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        name={id}
        type={id === "email" ? "email" : id === "phone" ? "tel" : "text"}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        autoComplete={autoComplete}
        inputMode={inputMode}
        required={required}
        className={`h-[50px] rounded-xl border bg-white px-4 text-[13px] leading-[24.4px] text-ink placeholder:text-muted ${
          invalid ? "border-danger-line" : "border-line"
        }`}
      />
      {invalid ? (
        <p id={errorId} className="text-[11px] font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function TextArea({
  id,
  label,
  placeholder,
  value,
  error,
  errorId,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  error: string | null;
  errorId: string;
  onChange: (value: string) => void;
}) {
  const invalid = error !== null;
  return (
    <div className="flex flex-col gap-[7px]">
      <label htmlFor={id} className="text-[13px] font-semibold leading-[24.4px] text-navy">
        {label}
      </label>
      <textarea
        id={id}
        name={id}
        rows={3}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        className={`min-h-[86px] resize-none rounded-xl border bg-white px-4 py-3 text-[13px] leading-[24.4px] text-ink placeholder:text-muted ${
          invalid ? "border-danger-line" : "border-line"
        }`}
      />
      {invalid ? (
        <p id={errorId} className="text-[11px] font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
